<template>
  <el-dialog
    v-model="visible"
    title="修改密码"
    width="450px"
    :before-close="handleClose"
  >
    <el-form
      ref="passwordFormRef"
      :model="passwordForm"
      :rules="passwordRules"
      label-width="100px"
      class="password-form"
    >
      <el-form-item label="当前密码" prop="current_password">
        <el-input
          v-model="passwordForm.current_password"
          type="password"
          placeholder="请输入当前密码"
          show-password
          maxlength="50"
        />
      </el-form-item>
      
      <el-form-item label="新密码" prop="new_password">
        <el-input
          v-model="passwordForm.new_password"
          type="password"
          placeholder="请输入新密码"
          show-password
          maxlength="50"
        />
        <div class="password-tips">
          <p>密码要求：</p>
          <ul>
            <li>长度至少8位</li>
            <li>包含大小写字母、数字</li>
            <li>可包含特殊字符</li>
          </ul>
        </div>
      </el-form-item>
      
      <el-form-item label="确认密码" prop="confirm_password">
        <el-input
          v-model="passwordForm.confirm_password"
          type="password"
          placeholder="请再次输入新密码"
          show-password
          maxlength="50"
        />
      </el-form-item>
    </el-form>
    
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleSave"
          :loading="saving"
        >
          确认修改
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { authAPI } from '@/services/api'

export default {
  name: 'PasswordDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const router = useRouter()
    const store = useStore()
    const passwordFormRef = ref()
    const saving = ref(false)
    
    const visible = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })
    
    const passwordForm = reactive({
      current_password: '',
      new_password: '',
      confirm_password: ''
    })
    
    // 验证新密码强度
    const validateNewPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入新密码'))
        return
      }
      
      if (value.length < 8) {
        callback(new Error('密码长度至少8位'))
        return
      }
      
      // 检查是否包含大小写字母和数字
      const hasLower = /[a-z]/.test(value)
      const hasUpper = /[A-Z]/.test(value)
      const hasNumber = /\d/.test(value)
      
      if (!hasLower || !hasUpper || !hasNumber) {
        callback(new Error('密码必须包含大小写字母和数字'))
        return
      }
      
      callback()
    }
    
    // 验证确认密码
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请确认新密码'))
        return
      }
      
      if (value !== passwordForm.new_password) {
        callback(new Error('两次输入的密码不一致'))
        return
      }
      
      callback()
    }
    
    const passwordRules = {
      current_password: [
        { required: true, message: '请输入当前密码', trigger: 'blur' }
      ],
      new_password: [
        { required: true, validator: validateNewPassword, trigger: 'blur' }
      ],
      confirm_password: [
        { required: true, validator: validateConfirmPassword, trigger: 'blur' }
      ]
    }
    
    // 保存密码修改
    const handleSave = async () => {
      try {
        await passwordFormRef.value.validate()
        
        saving.value = true
        
        const response = await authAPI.changePassword({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
        
        if (response.data.success) {
          ElMessage.success('密码修改成功')
          
          // 提示用户重新登录
          await ElMessageBox.confirm(
            '密码修改成功，为了安全起见，请重新登录。',
            '提示',
            {
              confirmButtonText: '重新登录',
              cancelButtonText: '稍后登录',
              type: 'success'
            }
          )
          
          // 清除登录状态并跳转到登录页
          await store.dispatch('auth/logout')
          router.push('/login')
          
        } else {
          ElMessage.error(response.data.message || '密码修改失败')
        }
      } catch (error) {
        if (error !== false && error !== 'cancel') {
          console.error('修改密码失败:', error)
          ElMessage.error('修改密码失败')
        }
      } finally {
        saving.value = false
      }
    }
    
    const handleClose = () => {
      // 重置表单
      passwordForm.current_password = ''
      passwordForm.new_password = ''
      passwordForm.confirm_password = ''
      
      // 清除验证状态
      if (passwordFormRef.value) {
        passwordFormRef.value.clearValidate()
      }
      
      visible.value = false
    }
    
    return {
      visible,
      passwordFormRef,
      saving,
      passwordForm,
      passwordRules,
      handleSave,
      handleClose
    }
  }
}
</script>

<style scoped>
.password-form {
  padding: 0 20px;
}

.password-tips {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.password-tips p {
  margin: 0 0 4px 0;
  font-weight: 600;
}

.password-tips ul {
  margin: 0;
  padding-left: 16px;
}

.password-tips li {
  margin-bottom: 2px;
}

.dialog-footer {
  text-align: right;
}

:deep(.el-form-item__label) {
  font-weight: 600;
}
</style>
